<script setup>
import { reactive, ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164, isIPv4 } from 'shared/helpers/Validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const uiFlags = useMapGetter('inboxes/getUIFlags');

const step = ref('credentials');
const inboxId = ref(null);
const loginSessionId = ref('');
const phoneNumberMasked = ref('');

const state = reactive({
  phoneNumber: '',
  appId: '',
  appHash: '',
  egressBindIp: '',
  code: '',
  password: '',
});

const credentialsRules = {
  phoneNumber: { required, isPhoneE164 },
  appId: { required },
  appHash: { required },
  egressBindIp: { required, isIPv4 },
};
const codeRules = { code: { required } };
const passwordRules = { password: { required } };

const vCredentials = useVuelidate(credentialsRules, state);
const vCode = useVuelidate(codeRules, state);
const vPassword = useVuelidate(passwordRules, state);

const credentialsErrors = computed(() => ({
  phoneNumber: vCredentials.value.phoneNumber?.$error
    ? t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE_NUMBER.ERROR')
    : '',
  appId: vCredentials.value.appId?.$error
    ? t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.APP_ID.ERROR')
    : '',
  appHash: vCredentials.value.appHash?.$error
    ? t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.APP_HASH.ERROR')
    : '',
  egressBindIp: vCredentials.value.egressBindIp?.$error
    ? t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.EGRESS_BIND_IP.ERROR')
    : '',
}));

const codeError = computed(() =>
  vCode.value.code?.$error
    ? t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.ERROR')
    : ''
);
const passwordError = computed(() =>
  vPassword.value.password?.$error
    ? t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.ERROR')
    : ''
);

const isSubmitDisabled = computed(() => {
  if (step.value === 'credentials') return vCredentials.value.$invalid;
  if (step.value === 'code') return vCode.value.$invalid;
  return vPassword.value.$invalid;
});

function clearSensitiveState() {
  state.password = '';
  state.code = '';
  loginSessionId.value = '';
  phoneNumberMasked.value = '';
}

onUnmounted(() => {
  clearSensitiveState();
});

async function startLogin() {
  const isFormValid = await vCredentials.value.$validate();
  if (!isFormValid) return;

  try {
    if (!inboxId.value) {
      const inbox = await store.dispatch('inboxes/createChannel', {
        channel: {
          type: 'telegram_user',
          phone_number: state.phoneNumber,
          app_id: Number(state.appId),
          app_hash: state.appHash,
          egress_bind_ip: state.egressBindIp,
        },
      });
      inboxId.value = inbox.id;
    }

    const response = await store.dispatch('inboxes/startTelegramUserLogin', {
      inboxId: inboxId.value,
    });

    loginSessionId.value = response.login_session_id;
    phoneNumberMasked.value = response.phone_number_masked;
    step.value = 'code';
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.API.ERROR_MESSAGE')
    );
  }
}

async function verifyCode() {
  const isFormValid = await vCode.value.$validate();
  if (!isFormValid) return;

  try {
    const response = await store.dispatch(
      'inboxes/verifyTelegramUserLoginCode',
      {
        inboxId: inboxId.value,
        loginSessionId: loginSessionId.value,
        code: state.code,
      }
    );

    if (response.next_step === 'LOGIN_NEXT_STEP_ENTER_PASSWORD') {
      step.value = 'password';
    } else {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: inboxId.value },
      });
    }
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.VERIFY_API.ERROR_MESSAGE')
    );
  }
}

async function verifyPassword() {
  const isFormValid = await vPassword.value.$validate();
  if (!isFormValid) return;

  try {
    await store.dispatch('inboxes/verifyTelegramUserLoginPassword', {
      inboxId: inboxId.value,
      loginSessionId: loginSessionId.value,
      password: state.password,
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inboxId.value },
    });
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.VERIFY_API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.DESC')"
    />

    <p class="text-sm text-n-slate-11 mb-4">
      {{ t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.MULTI_ACCOUNT_WARNING') }}
    </p>

    <form
      v-if="step === 'credentials'"
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="startLogin"
    >
      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE_NUMBER.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE_NUMBER.PLACEHOLDER')
        "
        :message="credentialsErrors.phoneNumber"
        :message-type="credentialsErrors.phoneNumber ? 'error' : 'info'"
        @blur="vCredentials.phoneNumber?.$touch"
      />

      <Input
        v-model="state.appId"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.APP_ID.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.APP_ID.PLACEHOLDER')
        "
        :message="credentialsErrors.appId"
        :message-type="credentialsErrors.appId ? 'error' : 'info'"
        @blur="vCredentials.appId?.$touch"
      />

      <Input
        v-model="state.appHash"
        type="password"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.APP_HASH.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.APP_HASH.PLACEHOLDER')
        "
        :message="credentialsErrors.appHash"
        :message-type="credentialsErrors.appHash ? 'error' : 'info'"
        @blur="vCredentials.appHash?.$touch"
      />

      <Input
        v-model="state.egressBindIp"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.EGRESS_BIND_IP.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.EGRESS_BIND_IP.PLACEHOLDER')
        "
        :message="credentialsErrors.egressBindIp"
        :message-type="credentialsErrors.egressBindIp ? 'error' : 'info'"
        @blur="vCredentials.egressBindIp?.$touch"
      />

      <div>
        <NextButton
          :is-loading="
            uiFlags.isCreating || uiFlags.isStartingTelegramUserLogin
          "
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SEND_CODE')"
          type="submit"
        />
      </div>
    </form>

    <form
      v-else-if="step === 'code'"
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="verifyCode"
    >
      <p v-if="phoneNumberMasked" class="text-sm text-n-slate-11">
        {{ phoneNumberMasked }}
      </p>

      <Input
        v-model="state.code"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.PLACEHOLDER')
        "
        :message="codeError"
        :message-type="codeError ? 'error' : 'info'"
        @blur="vCode.code?.$touch"
      />

      <div>
        <NextButton
          :is-loading="uiFlags.isVerifyingTelegramUserLogin"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.VERIFY_CODE')"
          type="submit"
        />
      </div>

      <NextButton
        variant="text"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RESEND_CODE')"
        type="button"
        @click="startLogin"
      />
    </form>

    <form
      v-else-if="step === 'password'"
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="verifyPassword"
    >
      <p v-if="phoneNumberMasked" class="text-sm text-n-slate-11">
        {{ phoneNumberMasked }}
      </p>

      <Input
        v-model="state.password"
        type="password"
        autocomplete="off"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.PLACEHOLDER')
        "
        :message="passwordError"
        :message-type="passwordError ? 'error' : 'info'"
        @blur="vPassword.password?.$touch"
      />

      <div>
        <NextButton
          :is-loading="uiFlags.isVerifyingTelegramUserPassword"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SUBMIT_PASSWORD')"
          type="submit"
        />
      </div>

      <NextButton
        variant="text"
        :label="t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RESEND_CODE')"
        type="button"
        @click="startLogin"
      />
    </form>
  </div>
</template>
